<template>
  <div
    :class="[`editable-board`, { 'editable-board--no-header': !header }]"
    :style="`--editable-board-layout: ${gridLayout}; 
    --editable-board-columns: ${columns.length + 1};`"
  >
    <div class="editable-board__header editable-board__row" v-if="header">
      <div v-if="sortable" class="editable-board__cell"></div>

      <div
        :class="['editable-board__cell', `editable-board__cell--text-align-${col.align || 'start'}`]"
        :key="col.id"
        :data-cell-header-id="col.id"
        v-for="col in columns.filter(({ disabled }) => !disabled)"
      >
        <slot :name="`${col.id}-label`" :item="col">
          <span v-html="col.label"></span>
        </slot>
      </div>
    </div>

    <Draggable
      :value="itemsComp"
      @input="updateSort"
      class="editable-board__draggable"
      handle=".editable-board__draggable-handle"
      :animation="150"
      :disabled="!sortable"
    >
      <div
        :class="['editable-board__row', { 'editable-board__row--delete': item.value === 'null' }]"
        v-for="(item, index) in itemsComp"
        :key="index"
      >
        <component
          :is="rowWrapper(item, index).component"
          class="editable-board__row-wrapper"
          v-bind="rowWrapper(item, index).props"
          v-on="rowWrapper(item, index).listeners"
        >
          <component
            :is="cellWrapper(item, col).component"
            v-bind="cellWrapper(item, col).props"
            v-on="cellWrapper(item, col).listeners"
            :class="`editable-board__cell editable-board__cell--text-align-${col.align || 'start'}`"
            v-for="(col, colIndex) in columns.filter(({ disabled }) => !disabled)"
            :key="`col-${col.id}`"
            :data-cell-id="col.id"
          >
            <slot :name="col.id" :item="item" :column="col" :row-index="index" :col-index="colIndex" />
          </component>
          <div v-if="sortable" class="editable-board__draggable-handle">
            <FontAwesomeIcon :icon="['fal', 'arrows-up-down']" />
          </div>
        </component>
      </div>
    </Draggable>

    <ButtonComponent
      v-if="addFields"
      class="editable-board__add"
      :label="createLabel || $t('createNewField')"
      :icon="['fas', 'plus-circle']"
      icon-placement="left"
      @click="add()"
    >
    </ButtonComponent>
  </div>
</template>

<script>
import ButtonGroup from '@kvass/button-group'
import Draggable from 'vuedraggable'

export default {
  props: {
    value: {
      type: Array,
      required: true,
    },
    columns: {
      type: Array,
      required: true,
    },
    addFields: {
      type: Boolean,
      default: true,
    },
    sortable: {
      type: Boolean,
      default: true,
    },
    customAdd: {
      type: Function,
    },
    createLabel: {
      type: String,
    },

    cellWrapper: {
      type: Function,
      default: () => ({ component: 'div' }),
    },
    rowWrapper: {
      type: Function,
      default: () => ({ component: 'div' }),
    },

    header: {
      type: Boolean,
      default: true,
    },
    isDisabled: {
      type: Function,
      default: () => false,
    },
  },

  data() {
    return {
      items: [],
      sorting: [],
    }
  },

  computed: {
    itemsComp() {
      return [...this.value, ...this.items]
    },

    gridLayout() {
      return [
        ...this.columns.filter(({ disabled }) => !disabled).map(c => c.size || 'auto'),
        this.sortable ? '4rem' : '',
      ]
        .filter(Boolean)
        .join(' ')
    },
  },
  methods: {
    add() {
      if (this.customAdd) return this.customAdd()
      this.items.push({ key: '', label: null, value: '' })
    },
    updateSort(items) {
      this.$emit('update-order', items)
    },
  },

  components: {
    ButtonGroup,
    Draggable,
  },
}
</script>

<style lang="scss">
.editable-board {
  width: 100%;
  display: grid;
  position: relative;
  gap: var(--editable-board-gap, 1rem);

  grid-template-columns: var(--editable-board-layout);

  &__cell {
    display: flex;
    align-items: center;
    padding: 0.75rem;

    &--text-align {
      &-start {
        justify-content: start;
      }
      &-center {
        justify-content: center;
      }
      &-end {
        justify-content: end;
      }
    }

    .elder-input {
      flex-grow: 1;
    }
  }

  &__add {
    grid-column-end: span var(--editable-board-columns);

    background-color: rgba(black, 0.05) !important;
    border: none !important;
    justify-content: center;
    align-items: center;

    .elder-button__label {
      flex-grow: 0;
    }

    .elder-button__icon {
      font-size: 1.6rem;
      color: var(--primary);
    }
  }

  &__draggable {
    display: contents;

    &-handle {
      background-color: var(--light-grey);
      margin: auto 0.5rem;
      height: 2rem;
      width: 2rem;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: move;
      border-radius: var(--border-radius);
    }
  }

  &__row {
    display: grid;
    grid-template-columns: var(--editable-board-layout);
    grid-column-end: span var(--editable-board-columns);
    background-color: css-lighten('border-color', 8%);
    border-radius: var(--border-radius);

    &.sortable-chosen {
      opacity: 0.25;
    }
    &-wrapper {
      display: contents;
    }
    &--delete {
      background-color: css-alpha('error', 0.07);
      color: var(--error);
      border: 1px solid var(--error);
      .elder-input {
        color: inherit;
      }
    }
  }

  &__header {
    background-color: white;
    font-size: 0.8em;
    text-align: left;
  }
}
</style>
